import { useCallback, useEffect, useState } from "react";

import { logger } from "interfaces/logger";

import { supabase } from "apis/supabaseClient";

import { parseDocumentFilename } from "utils/document";
import { generateUuidFileName } from "utils/file";
import { formatBytes } from "utils/text";

export const uploadAvatar = async (
  file: File
): Promise<{ path: string; fileName: string }> => {
  const fileName = generateUuidFileName(file.name);

  const { data, error } = await supabase.storage
    .from("avatars")
    .upload(fileName, file, { upsert: true });

  if (error) {
    logger(error.message, "error");
    throw new Error(error.message);
  }

  return { path: data.path, fileName };
};

export const uploadPortfolioItem = async (
  file: File
): Promise<{ path: string; fileName: string }> => {
  const fileName = generateUuidFileName(file.name);

  const { data, error } = await supabase.storage
    .from("portfolio")
    .upload(fileName, file, { upsert: false });

  if (error) {
    logger(error.message, "error");
    throw new Error(error.message);
  }

  return { path: data.path, fileName };
};

export const uploadDocument = async (
  file: File
): Promise<{ path: string; fileName: string }> => {
  const fileName = parseDocumentFilename(file.name);

  const { data, error } = await supabase.storage
    .from("document")
    .upload(fileName, file, { upsert: false });

  if (error) {
    logger(error.message, "error");
    throw new Error(error.message);
  }

  return { path: data.path, fileName };
};

export const uploadFile = async (
  file: File
): Promise<{ path: string; fileName: string }> => {
  const fileName = generateUuidFileName(file.name);

  const { data, error } = await supabase.storage
    .from("file")
    .upload(fileName, file, { upsert: false });

  if (error) {
    logger(error.message, "error");
    throw new Error(error.message);
  }

  return { path: data.path, fileName };
};

type UploadStatus = "not_started" | "in_progress" | "done" | "error";
export type UploadingFile = {
  file: File;
  status: UploadStatus;
  filename: string;
  size: string;
};
export const useUploadDocuments = (): {
  files: UploadingFile[];
  uploadFiles: (files: File[]) => void;
  error?: string;
  reset: () => void;
} => {
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const [error, setError] = useState<string>();

  const uploadFiles = useCallback((newFiles: File[]) => {
    setError(undefined);
    const formattedFiles = newFiles.map((file) => ({
      file,
      status: "not_started" as UploadStatus,
      filename: parseDocumentFilename(file.name),
      size: formatBytes(file.size, 1),
    }));
    setFiles((state) => [...state, ...formattedFiles]);
  }, []);

  useEffect(() => {
    const upload = async () => {
      const filesToUpload = files.filter(
        ({ status }) => status === "not_started"
      );
      if (!filesToUpload.length) return;

      const responses = await Promise.all(
        filesToUpload.map(({ file, filename }) =>
          supabase.storage
            .from("document")
            .upload(filename, file, { upsert: false })
        )
      );
      const error = responses.find(({ error }) => !!error)?.error;
      if (error) {
        setError("Something went wrong when uploading some documents");
        logger(error.message, "error");
        // mark all files to upload as errores
        setFiles((state) =>
          state.map((f) =>
            f.status !== "not_started" ? f : { ...f, status: "error" }
          )
        );
      } else {
        // mark all files to upload as uploading
        setFiles((state) =>
          state.map((f) =>
            f.status !== "not_started" ? f : { ...f, status: "done" }
          )
        );
      }
    };
    upload();
  }, [files]);

  const reset = useCallback(() => {
    setFiles([]);
    setError(undefined);
  }, []);

  return {
    files,
    uploadFiles,
    error,
    reset,
  };
};

export const uploadPostMediaItem = async (
  file: File
): Promise<{ path: string; fileName: string }> => {
  const fileName = generateUuidFileName(file.name);

  const { data, error } = await supabase.storage
    .from("post-media")
    .upload(fileName, file, { upsert: false });

  if (error) {
    logger(error.message, "error");
    throw new Error(error.message);
  }

  return { path: data.path, fileName };
};

export const getBlobLocalUrlFromRemoteUrl = async (
  url: string
): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error("File not found");
  const blob = await response.blob();
  return window.URL.createObjectURL(blob);
};
